import { memo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SearchField } from '@library';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import useSearchParamFilterHelper from '../../hooks/useSearchParamFilterHelper';
import useCategory from '../../../core/queries/useCategory';
import useEffectWithoutMount from '../../../core/util/useEffectWithoutMount';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import styles from './FilterSearch.module.scss';

const FilterSearch = ({
    name,
    defaultExpand,
}: {
    name: string;
    defaultExpand?: boolean;
}) => {
    const intl = useIntl();
    const { categoryName } = useCategory();
    const { updateCurrentValues, selectedValues } =
        useSearchParamFilterHelper()('q');
    const [selectedValue] = selectedValues as [string | undefined];
    const [searchTerm, setSearchTerm] = useState(selectedValue ?? '');
    const [expand, setExpand] = useState(defaultExpand);
    const expanded = (isExpanded: boolean) => {
        setExpand(isExpanded);
    };

    useEffectWithoutMount(() => {
        setExpand(defaultExpand);
    }, [defaultExpand]);

    useEffectWithoutMount(() => {
        setSearchTerm(term => {
            if (selectedValue !== term) {
                return selectedValue ?? '';
            }

            return term;
        });
    }, [selectedValue]);

    useEffectWithoutMount(() => {
        const handler = setTimeout(() => {
            updateCurrentValues(searchTerm, false, true);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const initiateSearch = () => {
        updateCurrentValues(searchTerm, false, true);
    };

    return (
        <CollapsibleContent
            onExpand={expanded}
            expanded={expand}
            filterIcon
            title={
                <CollapsibleSideBarFilterTitle>
                    {name}
                </CollapsibleSideBarFilterTitle>
            }
            endArrow
        >
            {expand && (
                <div className={styles.filterSearchField}>
                    <SearchField
                        light
                        aria-label="search-in-category"
                        id="categorySearch"
                        value={searchTerm}
                        data-test-class="categorySearchField"
                        onChange={setSearchTerm}
                        placeholder={`In ${
                            categoryName ??
                            intl.formatMessage({ id: 'all categories' })
                        }`}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                initiateSearch();
                            }
                        }}
                    />
                </div>
            )}
        </CollapsibleContent>
    );
};

export default memo(withErrorBoundary(FilterSearch));
